import isString from 'lodash-es/isString';
import noop from 'lodash-es/noop';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import { encodeKey } from '@jane/shared-b2b/components';

import { groupKeysByAncestor } from './util/rules';

interface CategorySelectInputModalProviderProps {
  children: ReactNode;
  onDirty?: (dirtyState: boolean) => void;
  rules?: any;
}

interface CategorySelectInputModal {
  childrenByKey: Map<string, string[]>;
  expandAll: boolean;
  hideSubItems: Record<string, boolean>;
  indeterminates: Set<string>;
  onDirty?: (dirtyState: boolean) => void;
  selectionMap: Map<string, boolean>;
  setExpandAll: (expanded: boolean) => void;
  setHideSubItems: Dispatch<SetStateAction<Record<string, boolean>>>;
  setIndeterminates: (indeterminateList: Set<string>) => void;
  setSelectionMap: (newSelectionMap: Map<string, boolean>) => void;
}

const CategorySelectInputModalContext = createContext<CategorySelectInputModal>(
  {
    childrenByKey: new Map(),
    indeterminates: new Set(),
    hideSubItems: {},
    onDirty: noop,
    selectionMap: new Map(),
    setHideSubItems: noop,
    setIndeterminates: noop,
    setSelectionMap: noop,
    expandAll: false,
    setExpandAll: noop,
  }
);
CategorySelectInputModalContext.displayName = 'CategorySelectInputModal';

export const useCategorySelectInputModal = () =>
  useContext(CategorySelectInputModalContext);

const getDefaultSelectionMap = (rules = []) => {
  rules = rules || [];

  const defaultSelectionMap = new Map();

  rules.forEach((rule) => {
    const { kind, root_subtype, brand_subtype } = rule;

    const rootSegment =
      root_subtype || (brand_subtype ? '(Subcategory not set)' : undefined);
    const segments = [kind, rootSegment, brand_subtype].filter(Boolean);

    defaultSelectionMap.set(encodeKey(segments), true);
  });

  return defaultSelectionMap;
};

const defaultSelectionMap = (rules: any) =>
  getDefaultSelectionMap(isString(rules) ? [] : rules);

export const CategorySelectInputModalProvider = ({
  children,
  onDirty,
  rules,
}: CategorySelectInputModalProviderProps) => {
  const [expandAll, setExpandAll] = useState(false);
  const [hideSubItems, setHideSubItems] = useState<Record<string, boolean>>({});
  const [indeterminates, setIndeterminates] = useState(new Set<string>());
  const [selectionMap, setSelectionMap] = useState(defaultSelectionMap(rules));

  const childrenByKey = useMemo(
    () => groupKeysByAncestor(selectionMap.keys()),
    [selectionMap]
  );

  const contextValue = useMemo(
    () => ({
      childrenByKey,
      expandAll,
      hideSubItems,
      indeterminates,
      onDirty,
      selectionMap,
      setExpandAll,
      setHideSubItems,
      setIndeterminates,
      setSelectionMap,
    }),
    [
      childrenByKey,
      expandAll,
      hideSubItems,
      indeterminates,
      onDirty,
      selectionMap,
      setExpandAll,
      setHideSubItems,
      setIndeterminates,
      setSelectionMap,
    ]
  );

  return (
    <CategorySelectInputModalContext.Provider value={contextValue}>
      {children}
    </CategorySelectInputModalContext.Provider>
  );
};
